/**
 * DistrictMap Component - Interactive map of Sierra Leone
 * Shows mpox cases by district with heat map visualization
 */

import { DistrictStats } from '@/api/ApiService';

// Sierra Leone district boundaries (simplified coordinates)
const SIERRA_LEONE_DISTRICTS = {
  'WA': { // Western Area
    name: 'Western Area Urban',
    center: [8.484, -13.229],
    coordinates: [
      [8.550, -13.300], [8.550, -13.150], [8.400, -13.150], [8.400, -13.300]
    ]
  },
  'WR': { // Western Rural
    name: 'Western Area Rural',
    center: [8.400, -13.100],
    coordinates: [
      [8.500, -13.200], [8.500, -13.000], [8.300, -13.000], [8.300, -13.200]
    ]
  },
  'BO': { // Bo
    name: 'Bo District',
    center: [7.964, -11.738],
    coordinates: [
      [8.200, -12.000], [8.200, -11.400], [7.700, -11.400], [7.700, -12.000]
    ]
  },
  'BM': { // Bombali
    name: 'Bombali District',
    center: [9.217, -12.583],
    coordinates: [
      [9.500, -13.000], [9.500, -12.200], [8.900, -12.200], [8.900, -13.000]
    ]
  },
  'KE': { // Kenema
    name: 'Kenema District',
    center: [7.876, -11.190],
    coordinates: [
      [8.200, -11.500], [8.200, -10.800], [7.500, -10.800], [7.500, -11.500]
    ]
  },
  'TO': { // Tonkolili
    name: 'Tonkolili District',
    center: [8.641, -11.796],
    coordinates: [
      [9.000, -12.200], [9.000, -11.400], [8.300, -11.400], [8.300, -12.200]
    ]
  },
  'KL': { // Kailahun
    name: 'Kailahun District',
    center: [8.279, -10.573],
    coordinates: [
      [8.600, -10.900], [8.600, -10.200], [7.900, -10.200], [7.900, -10.900]
    ]
  },
  'PU': { // Pujehun
    name: 'Pujehun District',
    center: [7.353, -11.720],
    coordinates: [
      [7.700, -12.100], [7.700, -11.300], [7.000, -11.300], [7.000, -12.100]
    ]
  },
  'MO': { // Moyamba
    name: 'Moyamba District',
    center: [8.158, -12.430],
    coordinates: [
      [8.500, -12.800], [8.500, -12.000], [7.800, -12.000], [7.800, -12.800]
    ]
  },
  'KM': { // Kambia
    name: 'Kambia District',
    center: [9.125, -12.918],
    coordinates: [
      [9.400, -13.200], [9.400, -12.600], [8.800, -12.600], [8.800, -13.200]
    ]
  },
  'KO': { // Kono
    name: 'Kono District',
    center: [8.641, -10.849],
    coordinates: [
      [9.000, -11.200], [9.000, -10.500], [8.300, -10.500], [8.300, -11.200]
    ]
  },
  'PL': { // Port Loko
    name: 'Port Loko District',
    center: [8.767, -12.787],
    coordinates: [
      [9.100, -13.100], [9.100, -12.500], [8.400, -12.500], [8.400, -13.100]
    ]
  },
  'BF': { // Bonthe
    name: 'Bonthe District',
    center: [7.526, -12.505],
    coordinates: [
      [7.800, -12.800], [7.800, -12.200], [7.200, -12.200], [7.200, -12.800]
    ]
  },
  'KD': { // Koinadugu
    name: 'Koinadugu District',
    center: [9.574, -11.371],
    coordinates: [
      [9.900, -11.800], [9.900, -10.900], [9.200, -10.900], [9.200, -11.800]
    ]
  },
  'FT': { // Falaba
    name: 'Falaba District',
    center: [9.787, -10.734],
    coordinates: [
      [10.100, -11.100], [10.100, -10.400], [9.400, -10.400], [9.400, -11.100]
    ]
  },
  'KN': { // Karene
    name: 'Karene District',
    center: [9.200, -12.000],
    coordinates: [
      [9.500, -12.400], [9.500, -11.600], [8.900, -11.600], [8.900, -12.400]
    ]
  }
};

export class DistrictMap {
  private container: HTMLElement | null = null;
  private districtData: DistrictStats[] = [];
  private selectedDistrict: string | null = null;
  private tooltip: HTMLElement | null = null;

  render(container: HTMLElement): void {
    this.container = container;
    
    container.innerHTML = `
      <div class="map-container">
        <div class="map-header">
          <div class="map-title">Sierra Leone Districts</div>
          <div class="map-legend">
            <div class="legend-title">Cases per District</div>
            <div class="legend-scale">
              <div class="legend-item">
                <div class="legend-color low"></div>
                <span>0-10</span>
              </div>
              <div class="legend-item">
                <div class="legend-color medium"></div>
                <span>11-50</span>
              </div>
              <div class="legend-item">
                <div class="legend-color high"></div>
                <span>51-100</span>
              </div>
              <div class="legend-item">
                <div class="legend-color critical"></div>
                <span>100+</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="map-svg-container">
          <svg 
            id="sierra-leone-map" 
            viewBox="0 0 800 600" 
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Interactive map of Sierra Leone showing mpox cases by district"
          >
            <!-- Map districts will be rendered here -->
          </svg>
        </div>
        
        <!-- Mobile district list -->
        <div class="mobile-district-list">
          <h3>Districts Overview</h3>
          <div id="district-list"></div>
        </div>
      </div>
      
      <!-- Tooltip for hover information -->
      <div id="map-tooltip" class="map-tooltip" style="display: none;"></div>
    `;

    this.createTooltip();
    this.addStyles();
    
    // Add a fallback if SVG rendering fails
    try {
      this.renderMap();
    } catch (error) {
      console.error('Map rendering failed, showing fallback:', error);
      this.showMapFallback();
    }
  }

  private createTooltip(): void {
    this.tooltip = this.container?.querySelector('#map-tooltip') || null;
  }

  private renderMap(): void {
    const svg = this.container?.querySelector('#sierra-leone-map');
    if (!svg) return;

    // Clear existing content
    svg.innerHTML = '';

    // Add background
    const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    background.setAttribute('width', '800');
    background.setAttribute('height', '600');
    background.setAttribute('fill', '#f8fafc');
    background.setAttribute('stroke', '#e2e8f0');
    svg.appendChild(background);

    // Render each district
    Object.entries(SIERRA_LEONE_DISTRICTS).forEach(([code, district]) => {
      const districtGroup = this.createDistrictElement(code, district);
      svg.appendChild(districtGroup);
    });

    // Add title
    const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    title.setAttribute('x', '400');
    title.setAttribute('y', '30');
    title.setAttribute('text-anchor', 'middle');
    title.setAttribute('class', 'map-title-svg');
    title.textContent = 'Sierra Leone - Mpox Cases by District';
    svg.appendChild(title);
  }

  private createDistrictElement(code: string, district: any): SVGElement {
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('class', 'district-group');
    group.setAttribute('data-district', code);

    // Convert coordinates to SVG coordinates
    const svgCoords = district.coordinates.map((coord: number[]) => [
      (coord[1] + 14) * 50, // Longitude to X (rough conversion)
      (11 - coord[0]) * 50  // Latitude to Y (inverted, rough conversion)
    ]);

    // Create polygon for district boundary
    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    const points = svgCoords.map((coord: number[]) => `${coord[0]},${coord[1]}`).join(' ');
    polygon.setAttribute('points', points);
    polygon.setAttribute('class', 'district-polygon');
    polygon.setAttribute('data-district', code);

    // Add event listeners
    polygon.addEventListener('mouseenter', (e) => this.handleDistrictHover(e, code));
    polygon.addEventListener('mouseleave', () => this.hideTooltip());
    polygon.addEventListener('mousemove', (e) => this.updateTooltipPosition(e));
    polygon.addEventListener('click', () => this.handleDistrictClick(code));

    group.appendChild(polygon);

    // Add district label
    const centerCoords = [
      (district.center[1] + 14) * 50,
      (11 - district.center[0]) * 50
    ];

    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', centerCoords[0].toString());
    label.setAttribute('y', centerCoords[1].toString());
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('class', 'district-label');
    label.textContent = code;

    // Add click handler to label too
    label.addEventListener('click', () => this.handleDistrictClick(code));

    group.appendChild(label);

    return group;
  }

  private handleDistrictHover(event: MouseEvent, districtCode: string): void {
    const districtStats = this.districtData.find(d => d.district_code === districtCode);
    if (!districtStats || !this.tooltip) return;

    const district = SIERRA_LEONE_DISTRICTS[districtCode as keyof typeof SIERRA_LEONE_DISTRICTS];
    if (!district) return;

    this.tooltip.innerHTML = `
      <div class="tooltip-header">
        <strong>${district.name}</strong>
      </div>
      <div class="tooltip-content">
        <div class="tooltip-stat">
          <span class="tooltip-label">Total Cases:</span>
          <span class="tooltip-value">${districtStats.total_cases.toLocaleString()}</span>
        </div>
        <div class="tooltip-stat">
          <span class="tooltip-label">Active:</span>
          <span class="tooltip-value">${districtStats.active_cases.toLocaleString()}</span>
        </div>
        <div class="tooltip-stat">
          <span class="tooltip-label">Deaths:</span>
          <span class="tooltip-value">${districtStats.deaths.toLocaleString()}</span>
        </div>
        <div class="tooltip-stat">
          <span class="tooltip-label">Vaccinated:</span>
          <span class="tooltip-value">${districtStats.vaccinated.toLocaleString()}</span>
        </div>
      </div>
      <div class="tooltip-footer">
        Click to select district
      </div>
    `;

    this.tooltip.style.display = 'block';
    this.updateTooltipPosition(event);
  }

  private hideTooltip(): void {
    if (this.tooltip) {
      this.tooltip.style.display = 'none';
    }
  }

  private updateTooltipPosition(event: MouseEvent): void {
    if (!this.tooltip) return;

    const rect = this.container?.getBoundingClientRect();
    if (!rect) return;

    const x = event.clientX - rect.left + 15;
    const y = event.clientY - rect.top - 10;

    // Keep tooltip within container bounds
    const tooltipRect = this.tooltip.getBoundingClientRect();
    const maxX = rect.width - tooltipRect.width - 10;
    const maxY = rect.height - tooltipRect.height - 10;

    this.tooltip.style.left = `${Math.min(x, maxX)}px`;
    this.tooltip.style.top = `${Math.max(10, Math.min(y, maxY))}px`;
  }

  private handleDistrictClick(districtCode: string): void {
    this.selectDistrict(districtCode);

    // Dispatch event for other components
    const event = new CustomEvent('map:district-click', {
      detail: { districtCode }
    });
    document.dispatchEvent(event);
  }

  selectDistrict(districtCode: string): void {
    this.selectedDistrict = districtCode;

    // Update visual selection
    this.container?.querySelectorAll('.district-polygon').forEach(polygon => {
      polygon.classList.remove('selected');
      if (polygon.getAttribute('data-district') === districtCode) {
        polygon.classList.add('selected');
      }
    });

    // Update mobile list selection
    this.updateMobileList();
  }

  async updateData(districts: DistrictStats[]): Promise<void> {
    this.districtData = districts;

    // Update district colors based on case counts
    districts.forEach(district => {
      const polygon = this.container?.querySelector(
        `.district-polygon[data-district="${district.district_code}"]`
      );
      
      if (polygon) {
        // Remove existing intensity classes
        polygon.classList.remove('intensity-low', 'intensity-medium', 'intensity-high', 'intensity-critical');
        
        // Add new intensity class based on case count
        const intensity = this.getIntensityClass(district.total_cases);
        polygon.classList.add(intensity);
      }
    });

    // Update mobile district list
    this.updateMobileList();
  }

  private getIntensityClass(cases: number): string {
    if (cases >= 100) return 'intensity-critical';
    if (cases >= 51) return 'intensity-high';
    if (cases >= 11) return 'intensity-medium';
    return 'intensity-low';
  }

  private updateMobileList(): void {
    const listContainer = this.container?.querySelector('#district-list');
    if (!listContainer) return;

    const sortedDistricts = [...this.districtData]
      .sort((a, b) => b.total_cases - a.total_cases);

    listContainer.innerHTML = sortedDistricts.map(district => {
      const isSelected = district.district_code === this.selectedDistrict;
      const intensityClass = this.getIntensityClass(district.total_cases);
      
      return `
        <div class="mobile-district-item ${isSelected ? 'selected' : ''}" 
             data-district="${district.district_code}">
          <div class="district-info">
            <div class="district-name">
              <span class="district-code">${district.district_code}</span>
              <span class="district-full-name">${district.district_name}</span>
            </div>
            <div class="district-cases ${intensityClass}">
              ${district.total_cases} cases
            </div>
          </div>
          <div class="district-details">
            <span>Active: ${district.active_cases}</span>
            <span>Deaths: ${district.deaths}</span>
            <span>Vaccinated: ${district.vaccinated}</span>
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers to mobile list items
    listContainer.querySelectorAll('.mobile-district-item').forEach(item => {
      item.addEventListener('click', () => {
        const districtCode = item.getAttribute('data-district');
        if (districtCode) {
          this.handleDistrictClick(districtCode);
        }
      });
    });
  }

  showLoading(): void {
    if (!this.container) return;

    const mapContainer = this.container.querySelector('.map-svg-container');
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div class="map-loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">Loading map data...</div>
        </div>
      `;
    }
  }

  private showMapFallback(): void {
    if (!this.container) return;

    const mapContainer = this.container.querySelector('.map-svg-container');
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div class="map-fallback">
          <div class="fallback-icon">🗺️</div>
          <div class="fallback-title">Interactive Map</div>
          <div class="fallback-text">
            Map visualization is loading. District data is available below.
          </div>
        </div>
      `;
    }
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .map-container {
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow);
        overflow: hidden;
      }
      
      .map-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-4);
        background: var(--primary-green);
        color: white;
      }
      
      @media (max-width: 768px) {
        .map-header {
          flex-direction: column;
          gap: var(--space-3);
          text-align: center;
        }
      }
      
      .map-title {
        font-size: var(--text-lg);
        font-weight: 600;
      }
      
      .map-legend {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .legend-title {
        font-size: var(--text-sm);
        font-weight: 500;
      }
      
      .legend-scale {
        display: flex;
        gap: var(--space-3);
      }
      
      @media (max-width: 768px) {
        .legend-scale {
          justify-content: center;
          flex-wrap: wrap;
          gap: var(--space-2);
        }
      }
      
      .legend-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-size: var(--text-xs);
      }
      
      .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid rgba(255, 255, 255, 0.3);
      }
      
      .legend-color.low { background: #10b981; }
      .legend-color.medium { background: #f59e0b; }
      .legend-color.high { background: #ef4444; }
      .legend-color.critical { background: #7c2d12; }
      
      .map-svg-container {
        position: relative;
        padding: var(--space-4);
        min-height: 400px;
      }
      
      #sierra-leone-map {
        width: 100%;
        height: auto;
        max-height: 500px;
      }
      
      .district-polygon {
        fill: #e2e8f0;
        stroke: #94a3b8;
        stroke-width: 1;
        cursor: pointer;
        transition: all 0.2s ease;
      }
      
      .district-polygon:hover {
        stroke: var(--primary-green);
        stroke-width: 2;
        filter: brightness(1.1);
      }
      
      .district-polygon.selected {
        stroke: var(--primary-green);
        stroke-width: 3;
        filter: brightness(1.2);
      }
      
      .district-polygon.intensity-low {
        fill: #10b981;
      }
      
      .district-polygon.intensity-medium {
        fill: #f59e0b;
      }
      
      .district-polygon.intensity-high {
        fill: #ef4444;
      }
      
      .district-polygon.intensity-critical {
        fill: #7c2d12;
      }
      
      .district-label {
        font-size: 10px;
        font-weight: 600;
        fill: white;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
        cursor: pointer;
        pointer-events: all;
      }
      
      .map-title-svg {
        font-size: 16px;
        font-weight: 600;
        fill: var(--primary-green);
      }
      
      .map-tooltip {
        position: absolute;
        background: white;
        border-radius: var(--radius);
        box-shadow: var(--shadow-lg);
        padding: var(--space-3);
        font-size: var(--text-sm);
        z-index: 100;
        max-width: 200px;
        border: 1px solid var(--gray-200);
      }
      
      .tooltip-header {
        margin-bottom: var(--space-2);
        padding-bottom: var(--space-1);
        border-bottom: 1px solid var(--gray-200);
        color: var(--primary-green);
      }
      
      .tooltip-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .tooltip-stat {
        display: flex;
        justify-content: space-between;
      }
      
      .tooltip-label {
        color: var(--gray-600);
      }
      
      .tooltip-value {
        font-weight: 600;
        color: var(--gray-900);
      }
      
      .tooltip-footer {
        margin-top: var(--space-2);
        padding-top: var(--space-1);
        border-top: 1px solid var(--gray-200);
        font-size: var(--text-xs);
        color: var(--gray-500);
        text-align: center;
      }
      
      .mobile-district-list {
        display: none;
        padding: var(--space-4);
        border-top: 1px solid var(--gray-200);
      }
      
      @media (max-width: 768px) {
        .mobile-district-list {
          display: block;
        }
        
        .map-svg-container {
          display: none;
        }
      }
      
      .mobile-district-list h3 {
        margin-bottom: var(--space-4);
        color: var(--primary-green);
        text-align: center;
      }
      
      .mobile-district-item {
        padding: var(--space-3);
        border: 1px solid var(--gray-200);
        border-radius: var(--radius);
        margin-bottom: var(--space-2);
        cursor: pointer;
        transition: var(--transition);
      }
      
      .mobile-district-item:hover {
        background: var(--gray-50);
        border-color: var(--primary-green);
      }
      
      .mobile-district-item.selected {
        background: var(--primary-green);
        color: white;
        border-color: var(--primary-green);
      }
      
      .district-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-1);
      }
      
      .district-name {
        display: flex;
        flex-direction: column;
      }
      
      .district-code {
        font-weight: 700;
        font-size: var(--text-sm);
      }
      
      .district-full-name {
        font-size: var(--text-xs);
        opacity: 0.8;
      }
      
      .district-cases {
        font-weight: 700;
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-sm);
        font-size: var(--text-sm);
      }
      
      .district-cases.intensity-low {
        background: #10b981;
        color: white;
      }
      
      .district-cases.intensity-medium {
        background: #f59e0b;
        color: white;
      }
      
      .district-cases.intensity-high {
        background: #ef4444;
        color: white;
      }
      
      .district-cases.intensity-critical {
        background: #7c2d12;
        color: white;
      }
      
      .district-details {
        display: flex;
        justify-content: space-between;
        font-size: var(--text-xs);
        opacity: 0.8;
      }
      
      .mobile-district-item.selected .district-details {
        opacity: 1;
      }
      
      .map-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        gap: var(--space-4);
      }
      
      .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid var(--gray-200);
        border-top: 4px solid var(--primary-green);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      .loading-text {
        color: var(--gray-600);
        font-size: var(--text-sm);
      }
      
      .map-fallback {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        gap: var(--space-4);
        color: var(--gray-600);
      }
      
      .fallback-icon {
        font-size: 3rem;
      }
      
      .fallback-title {
        font-size: var(--text-lg);
        font-weight: 600;
        color: var(--gray-700);
      }
      
      .fallback-text {
        text-align: center;
        max-width: 300px;
      }
      
      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        .district-polygon,
        .loading-spinner {
          transition: none;
          animation: none;
        }
      }
      
      /* High contrast mode */
      @media (prefers-contrast: high) {
        .district-polygon {
          stroke-width: 2;
        }
        
        .map-tooltip {
          border-width: 2px;
        }
      }
    `;
    
    document.head.appendChild(style);
  }
}